const controlRegistry = new Map();
const rootControlIds = new WeakMap();
let nextControlId = 1;

function createControlId() {
  const id = `control-${nextControlId}`;
  nextControlId += 1;
  return id;
}

function toControlId(controlOrId) {
  if (typeof controlOrId === "string") {return controlOrId;}
  return controlOrId?.id || "";
}

function isElement(value) {
  return Boolean(value && value.nodeType === Node.ELEMENT_NODE);
}

function addRootIndex(root, controlId) {
  if (!isElement(root)) {return;}
  const existingIds = rootControlIds.get(root) || new Set();
  existingIds.add(controlId);
  rootControlIds.set(root, existingIds);
}

function removeRootIndex(root, controlId) {
  if (!isElement(root)) {return;}
  const existingIds = rootControlIds.get(root);
  if (!existingIds) {return;}
  existingIds.delete(controlId);
  if (existingIds.size === 0) {
    rootControlIds.delete(root);
  }
}

function getControlsForScope(scope) {
  if (!scope || scope === document) {
    return Array.from(controlRegistry.values());
  }

  if (!isElement(scope)) {
    return [];
  }

  return Array.from(controlRegistry.values()).filter((control) => {
    return control.root === scope ||
      scope.contains(control.root) ||
      control.form === scope;
  });
}

function getRecord(controlOrId) {
  const controlId = toControlId(controlOrId);
  if (!controlId) {return null;}
  return controlRegistry.get(controlId) || null;
}

function normalizeValue(value, multiple = false) {
  if (multiple) {
    if (Array.isArray(value)) {return value;}
    if (value === null || value === undefined || value === "") {return [];}
    return [value];
  }

  if (value === null || value === undefined) {return "";}
  return value;
}

function getMirrorValue(value, control) {
  if (typeof control.mirrorFormatter === "function") {
    return control.mirrorFormatter(value, control);
  }

  if (Array.isArray(value) || typeof value === "object" && value !== null) {
    return JSON.stringify(value);
  }

  return String(value ?? "");
}

function setNativeInputValue(input, value, control) {
  if (!input) {return;}

  const normalizedValue = normalizeValue(value, control.multiple);
  const inputType = input.type;

  if (inputType === "checkbox") {
    input.checked = Array.isArray(normalizedValue) ?
      normalizedValue.includes(input.value) :
      Boolean(normalizedValue);
    return;
  }

  if (inputType === "radio") {
    input.checked = input.value === String(normalizedValue);
    return;
  }

  input.value = getMirrorValue(normalizedValue, control);
}

function resolveMirrorInput(definition) {
  if (definition.mirrorInput) {
    return {
      input: definition.mirrorInput,
      generated: false,
    };
  }
  if (definition.mirror === false) {
    return {
      input: null,
      generated: false,
    };
  }

  const form = definition.form ||
    definition.root?.closest?.("form") ||
    null;
  const name = definition.name || definition.mirrorName;

  if (!form || !name) {
    return {
      input: null,
      generated: false,
    };
  }

  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.dataset.controlMirror = definition.id || "";
  form.append(input);
  return {
    input,
    generated: true,
  };
}

function dispatchControlChange(control) {
  const changeEvent = new CustomEvent("control:valuechange", {
    bubbles: true,
    detail: {
      id: control.id,
      name: control.name,
      value: control.getValue(),
    },
  });

  control.root.dispatchEvent(changeEvent);

  if (control.mirrorInput) {
    control.mirrorInput.dispatchEvent(new Event("change", {bubbles: true}));
  }
}

function isRequiredValidationMessage(message) {
  const normalized = String(message || "").trim().toLowerCase();
  return normalized === "required" || normalized === "this field is required.";
}

function markValidationState(control, result) {
  const showInvalid = Boolean(control.validationVisible && !result.valid);
  control.validation = result;
  control.root.toggleAttribute("data-control-invalid", showInvalid);
  control.root.setAttribute("aria-invalid", String(showInvalid));

  if (control.mirrorInput) {
    control.mirrorInput.setCustomValidity(result.valid ? "" : result.message);
    control.mirrorInput.setAttribute("aria-invalid", String(showInvalid));
  }

  if (control.validationMessageElement) {
    const showMessage = Boolean(
      showInvalid &&
      result.message &&
      result.reason !== "required" &&
      !isRequiredValidationMessage(result.message),
    );
    control.validationMessageElement.textContent = showMessage ? result.message : "";
    control.validationMessageElement.classList.toggle("hidden", !showMessage);
  }
}

function normalizeValidationResult(result) {
  if (result === true || result === undefined || result === null) {
    return {valid: true, message: ""};
  }

  if (result === false) {
    return {valid: false, message: "This field is invalid."};
  }

  return {
    valid: result.valid !== false,
    message: result.message || "",
    reason: result.reason || "",
  };
}

function validateControl(control) {
  const value = control.getValue();
  const isEmptyArray = Array.isArray(value) && value.length === 0;
  const isEmptyValue = value === "" || value === null || value === undefined;

  if (control.required && (isEmptyArray || isEmptyValue)) {
    return {
      valid: false,
      message: control.requiredMessage || "This field is required.",
      reason: "required",
    };
  }

  if (typeof control.validate === "function") {
    return normalizeValidationResult(control.validate(value, control));
  }

  return {valid: true, message: ""};
}

function buildControlHandle(control) {
  return {
    id: control.id,
    name: control.name,
    get root() {return control.root;},
    get value() {return control.getValue();},
    setValue(value, options = {}) {
      return updateControlValue(control.id, value, options);
    },
    update(updates = {}) {
      return updateRegisteredControl(control.id, updates);
    },
    validate() {
      return validateRegisteredControl(control.id);
    },
    destroy() {
      destroyRegisteredControl(control.id);
    },
  };
}

function createControlRecord(definition) {
  if (!isElement(definition.root)) {
    throw new Error("registerControl requires a root element.");
  }

  const id = definition.id || createControlId();
  const initialValue = normalizeValue(definition.value, definition.multiple);
  let currentValue = initialValue;

  const control = {
    id,
    name: definition.name || definition.mirrorName || definition.mirrorInput?.name || "",
    root: definition.root,
    form: definition.form || definition.root.closest?.("form") || null,
    mirrorInput: null,
    mirrorFormatter: definition.mirrorFormatter,
    multiple: Boolean(definition.multiple),
    required: Boolean(definition.required),
    requiredMessage: definition.requiredMessage,
    disabled: Boolean(definition.disabled),
    validation: {valid: true, message: ""},
    validationVisible: false,
    validationMessageElement: definition.validationMessageElement || null,
    mounted: false,
    getValue: () => {
      if (typeof definition.getValue === "function") {
        return normalizeValue(definition.getValue(control), control.multiple);
      }
      return currentValue;
    },
    setValue: (value) => {
      currentValue = normalizeValue(value, control.multiple);
      if (typeof definition.setValue === "function") {
        definition.setValue(currentValue, control);
      }
    },
    mount: definition.mount,
    update: definition.update,
    destroy: definition.destroy,
    validate: definition.validate,
  };

  const mirror = resolveMirrorInput({...definition, id});
  control.mirrorInput = mirror.input;
  control.generatedMirrorInput = mirror.generated;
  return control;
}

/**
 * Registers an upgraded control and returns a lifecycle handle for it.
 * @param {object} definition Control registration details.
 * @param {HTMLElement} definition.root Root element owned by the visible control.
 * @param {string} [definition.id] Stable control id. Generated when omitted.
 * @param {string} [definition.name] Field name used during value extraction.
 * @param {*} [definition.value] Initial canonical value.
 * @param {boolean} [definition.multiple=false] Whether the canonical value is an array.
 * @param {boolean} [definition.required=false] Whether the control must have a value.
 * @param {boolean} [definition.disabled=false] Whether extraction should skip the control by default.
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} [definition.mirrorInput] Native input to mirror into.
 * @param {Function} [definition.getValue] Canonical value reader.
 * @param {Function} [definition.setValue] Canonical value writer.
 * @param {Function} [definition.mount] Mount callback.
 * @param {Function} [definition.update] Update callback.
 * @param {Function} [definition.destroy] Cleanup callback.
 * @param {Function} [definition.validate] Validation callback.
 * @param {HTMLElement} [definition.validationMessageElement] Inline validation message element.
 * @returns {object} Control lifecycle handle.
 */
export function registerControl(definition) {
  const control = createControlRecord(definition);

  if (controlRegistry.has(control.id)) {
    throw new Error(`Control ${control.id} is already registered.`);
  }

  controlRegistry.set(control.id, control);
  addRootIndex(control.root, control.id);
  control.root.dataset.controlId = control.id;

  if (control.mirrorInput) {
    control.mirrorInput.dataset.controlMirror = control.id;
    control.mirrorInput.disabled = control.disabled;
    setNativeInputValue(control.mirrorInput, control.getValue(), control);
  }

  if (typeof control.mount === "function") {
    control.mount(control);
  }

  control.mounted = true;
  markValidationState(control, {valid: true, message: ""});

  return buildControlHandle(control);
}

/**
 * Updates a registered control's lifecycle metadata and mirror state.
 * @param {string|object} controlOrId Registered control id or handle.
 * @param {object} updates Metadata and value updates.
 * @returns {object|null} Updated lifecycle handle, or null if the control is missing.
 */
export function updateRegisteredControl(controlOrId, updates = {}) {
  const control = getRecord(controlOrId);
  if (!control) {return null;}

  if (updates.root && updates.root !== control.root) {
    removeRootIndex(control.root, control.id);
    delete control.root.dataset.controlId;
    control.root = updates.root;
    addRootIndex(control.root, control.id);
    control.root.dataset.controlId = control.id;
  }

  if ("name" in updates) {control.name = updates.name;}
  if ("required" in updates) {control.required = Boolean(updates.required);}
  if ("disabled" in updates) {
    control.disabled = Boolean(updates.disabled);
    if (control.mirrorInput) {
      control.mirrorInput.disabled = control.disabled;
    }
  }
  if ("mirrorInput" in updates) {
    if (control.generatedMirrorInput && control.mirrorInput) {
      control.mirrorInput.remove();
    }
    control.mirrorInput = updates.mirrorInput;
    control.generatedMirrorInput = false;
  }
  if ("mirrorFormatter" in updates) {control.mirrorFormatter = updates.mirrorFormatter;}
  if ("requiredMessage" in updates) {control.requiredMessage = updates.requiredMessage;}
  if ("multiple" in updates) {control.multiple = Boolean(updates.multiple);}
  if ("validationMessageElement" in updates) {
    control.validationMessageElement = updates.validationMessageElement;
  }

  if ("value" in updates) {
    control.setValue(updates.value);
  }

  syncControlMirror(control.id);
  markValidationState(
    control,
    control.validationVisible ? validateControl(control) : {valid: true, message: ""},
  );

  if (typeof control.update === "function") {
    control.update(updates, control);
  }

  return buildControlHandle(control);
}

/**
 * Updates the canonical value for a registered control and syncs its mirror.
 * @param {string|object} controlOrId Registered control id or handle.
 * @param {*} value New canonical value.
 * @param {object} [options]
 * @param {boolean} [options.silent=false] Whether to suppress change events.
 * @returns {boolean} Whether the control was found.
 */
export function updateControlValue(controlOrId, value, options = {}) {
  const control = getRecord(controlOrId);
  if (!control) {return false;}

  control.setValue(value);
  syncControlMirror(control.id);
  markValidationState(
    control,
    control.validationVisible ? validateControl(control) : {valid: true, message: ""},
  );

  if (!options.silent) {
    dispatchControlChange(control);
  }

  return true;
}

/**
 * Syncs a registered control's canonical value into its native mirror input.
 * @param {string|object} controlOrId Registered control id or handle.
 * @returns {boolean} Whether a mirror was synced.
 */
export function syncControlMirror(controlOrId) {
  const control = getRecord(controlOrId);
  if (!control?.mirrorInput) {return false;}

  setNativeInputValue(control.mirrorInput, control.getValue(), control);
  return true;
}

/**
 * Validates one registered control and updates its validation state.
 * @param {string|object} controlOrId Registered control id or handle.
 * @returns {object|null} Validation result, or null if the control is missing.
 */
export function validateRegisteredControl(controlOrId) {
  const control = getRecord(controlOrId);
  if (!control) {return null;}

  const result = validateControl(control);
  control.validationVisible = true;
  markValidationState(control, result);
  return result;
}

/**
 * Validates every registered control inside a scope.
 * @param {Document|HTMLElement} [scope=document] Root scope to validate.
 * @param {object} [options]
 * @param {boolean} [options.includeDisabled=false] Include disabled controls.
 * @returns {object} Validation summary with control errors.
 */
export function validateRegisteredControls(scope = document, options = {}) {
  const errors = [];

  getControlsForScope(scope).forEach((control) => {
    if (control.disabled && !options.includeDisabled) {return;}

    const result = validateRegisteredControl(control.id);
    if (result && !result.valid) {
      errors.push({
        id: control.id,
        name: control.name,
        message: result.message,
      });
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Focuses the first invalid registered control from a validation summary.
 * @param {object} validation Validation summary from validateRegisteredControls.
 * @returns {void}
 */
export function focusFirstRegisteredControlError(validation) {
  const firstError = validation?.errors?.[0];
  const handle = firstError ? getRegisteredControl(firstError.id) : null;
  const focusTarget = handle?.root?.querySelector?.(
    "button, [tabindex], input, select, textarea",
  );

  if (typeof focusTarget?.focus === "function") {
    focusTarget.focus({preventScroll: true});
  }

  handle?.root?.scrollIntoView?.({
    block: "center",
    behavior: "smooth",
  });
}

/**
 * Extracts canonical values from registered upgraded controls in a scope.
 * @param {Document|HTMLElement} [scope=document] Root scope to extract from.
 * @param {object} [options]
 * @param {boolean} [options.includeDisabled=false] Include disabled controls.
 * @param {boolean} [options.validate=false] Validate before extracting.
 * @returns {object} Extracted values, validation errors, and source control ids.
 */
export function getRegisteredControlValues(scope = document, options = {}) {
  const values = {};
  const errors = [];
  const sources = {};

  getControlsForScope(scope).forEach((control) => {
    if (control.disabled && !options.includeDisabled) {return;}
    if (!control.name) {return;}

    if (options.validate) {
      const result = validateRegisteredControl(control.id);
      if (result && !result.valid) {
        errors.push({
          id: control.id,
          name: control.name,
          message: result.message,
        });
        return;
      }
    }

    values[control.name] = control.getValue();
    sources[control.name] = control.id;
  });

  return {
    values,
    errors,
    sources,
    valid: errors.length === 0,
  };
}

/**
 * Layers registered control values over an existing payload.
 * @param {object} payload Existing form or detail payload.
 * @param {Document|HTMLElement} [scope=document] Root scope to extract from.
 * @param {object} [options] Extraction options.
 * @returns {object} New payload with registered control values treated as canonical.
 */
export function mergeRegisteredControlValues(payload, scope = document, options = {}) {
  const extraction = getRegisteredControlValues(scope, options);
  return {
    ...payload,
    ...extraction.values,
  };
}

/**
 * Returns a registered control lifecycle handle.
 * @param {string|object} controlOrId Registered control id or handle.
 * @returns {object|null} Control lifecycle handle, or null if missing.
 */
export function getRegisteredControl(controlOrId) {
  const control = getRecord(controlOrId);
  return control ? buildControlHandle(control) : null;
}

/**
 * Destroys one registered control and removes generated mirror inputs.
 * @param {string|object} controlOrId Registered control id or handle.
 * @returns {boolean} Whether the control was found and destroyed.
 */
export function destroyRegisteredControl(controlOrId) {
  const control = getRecord(controlOrId);
  if (!control) {return false;}

  if (typeof control.destroy === "function") {
    control.destroy(control);
  }

  removeRootIndex(control.root, control.id);
  delete control.root.dataset.controlId;

  if (control.generatedMirrorInput && control.mirrorInput?.dataset.controlMirror === control.id) {
    control.mirrorInput.remove();
  } else if (control.mirrorInput?.dataset.controlMirror === control.id) {
    delete control.mirrorInput.dataset.controlMirror;
  }

  controlRegistry.delete(control.id);
  return true;
}

/**
 * Destroys every registered control inside a scope.
 * @param {Document|HTMLElement} [scope=document] Root scope to destroy.
 * @returns {number} Number of controls destroyed.
 */
export function destroyRegisteredControlsIn(scope = document) {
  const controls = getControlsForScope(scope);
  controls.forEach((control) => {
    destroyRegisteredControl(control.id);
  });
  return controls.length;
}
